import DOMPurify from 'dompurify';
import { linkificar } from './linkificar';

// Sanitização de todo HTML que a aplicação entrega a `dangerouslySetInnerHTML`.
//
// Duas origens, o mesmo risco:
//
//   Mensagem de chat — a API grava o campo `message` verbatim
//   (`MessageService::store()` não filtra) e `POST /api/messages` não exige
//   autenticação, porque é o widget público. O conteúdo é digitado por quem
//   estiver do outro lado da conversa.
//
//   Ação de chamado — o corpo aceita HTML do editor, mas também recebe o HTML
//   das mensagens importadas e o dos e-mails que viram chamado, escrito por
//   qualquer remetente.
//
// Sem filtro, um `<img src=x onerror="...">` executa na tela do operador.
// `<script>` não roda quando inserido via innerHTML, mas handlers de evento
// rodam — então escapar só a tag `script` não resolveria.
//
// A sanitização é feita na RENDERIZAÇÃO, não na gravação: o que está no banco
// continua intacto (inclusive o que veio da URA e do Smclick), e a barreira
// vale também para tudo o que já está gravado.
//
// IMPORTANTE para as imagens de ação: `blob:` NÃO passa pelo DOMPurify. Por
// isso a sanitização acontece ANTES da troca de `src` por blob URL, nunca
// depois — o blob é introduzido pelo nosso próprio código, e não precisa
// atravessar o filtro. Inverter essa ordem faz sumir toda imagem embutida.

/**
 * Devolve o HTML pronto para `dangerouslySetInnerHTML`.
 *
 * @param {string} html conteúdo a exibir
 * @returns {string} HTML sem scripts, handlers de evento ou URLs perigosas
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';

  const limpo = DOMPurify.sanitize(String(html));

  // Links abrem em nova aba para não perder o estado da tela (socket, sessão,
  // rascunho de ação) ao navegar. Depois da sanitização, de propósito: o
  // DOMPurify remove `target` do conteúdo de entrada, e é aqui que ele volta
  // — decidido por nós, e sempre com o `rel` que fecha o `window.opener`.
  const container = document.createElement('div');
  container.innerHTML = limpo;
  container.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  return container.innerHTML;
};

/**
 * O conteúdo de uma MENSAGEM DE CONVERSA, pronto para renderizar.
 *
 * Sanitiza e, em cima disso, transforma em link o endereço que a pessoa
 * digitou como texto (ponto 1 da Gisele: "links enviados ao cliente devem
 * aparecer como clicáveis").
 *
 * Função própria, e não um acréscimo ao `sanitizeHtml`: este também atende o
 * corpo de ação de chamado, o HTML dos e-mails importados e os textos das
 * Configurações. Fazer os dois juntos mudaria tudo isso de uma vez, sem
 * ninguém ter pedido.
 *
 * A ordem é obrigatória, e está explicada em `linkificar`: ele CRIA `<a>`, e o
 * `href` que ele monta não passaria pelo DOMPurify se viesse antes.
 *
 * @param {string} html conteúdo da mensagem, como veio da API
 * @returns {string} HTML seguro, com os endereços clicáveis
 */
export const sanitizeChatMessage = (html) => linkificar(sanitizeHtml(html));

export default sanitizeHtml;
